import { Container, IInit } from '../../../common/container/Container'
import { Event } from '../models/Event'
import { CalendarContainerConfig } from '../container'
import { Observable, of } from 'rxjs'
import { Query } from '../../../common/api/Query'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { catchError, map } from 'rxjs/operators'
import { EventDTO, toModel } from '../models/EventDTO'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { prepareURL } from '../../../common/api/http-helpers'
import { STATUS_SERVICE_KEY } from '../../../container/app'

export interface IEventApi extends IInit {
  getByID(id: string): Observable<Event | undefined>

  getFilteredList(q: Query<Event>, userId: string): Observable<ItemList<Event>>

  add(e: EventDTO): Observable<Event | undefined>

  update(e: EventDTO): Observable<Event | undefined>

  delete(id: string): Observable<boolean>

  createPDF(q: Query<Event>, userID: string): Observable<any | undefined>
}

export class EventApi implements IEventApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as CalendarContainerConfig).moduleFullUrl + '/events'
  }

  add(e: EventDTO): Observable<Event | undefined> {
    return this._httpClient
      .post<Event>({ url: this._url, body: e })
      .pipe(
        map<EventDTO, Event>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: this._url + '/' + id }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getByID(id: string): Observable<Event | undefined> {
    return this._httpClient
      .get<Event>({ url: `${this._url}/${id}` })
      .pipe(
        map<EventDTO, Event>((d) => toModel(d)),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  getFilteredList(q: Query<Event>, userId: string): Observable<ItemList<Event>> {
    return this._httpClient
      .get<ItemList<Event>>({ url: prepareURL(`${this._url}/${userId}/list`, q) })
      .pipe(
        map<ItemList<EventDTO>, ItemList<Event>>((dto) => {
          const itemList = emptyList<Event>()
          itemList.count = dto.count
          itemList.items = dto.items.map((d) => toModel(d))
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<Event>())
        })
      )
  }

  update(e: EventDTO): Observable<Event | undefined> {
    return this._httpClient
      .put<Event>({ url: this._url, body: e })
      .pipe(
        map<EventDTO, Event>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  createPDF(q: Query<Event>, userID: string): Observable<any | undefined> {
    return this._httpClient.get({ url: prepareURL(`${this._url}/${userID}/pdf`, q) }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }
}
